<template>
  <main
    class="flex flex-col w-full p-8 md:p-12 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen"
  >
    <div class="flex justify-between max-w-6xl mx-auto w-full mb-8">
      <RouterLink
        to="/"
        class="px-6 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all duration-300 font-medium flex items-center space-x-2 text-sm shadow-md hover:shadow-lg"
      >
        <i class="fas fa-cog"></i>
        <span>Configurations</span>
      </RouterLink>
      <RouterLink
        to="/tracking"
        class="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 font-medium flex items-center space-x-2 text-sm shadow-md hover:shadow-lg"
      >
        <i class="fas fa-chart-line"></i>
        <span>Track Links</span>
      </RouterLink>
    </div>
    <div class="flex flex-col space-y-8 max-w-6xl mx-auto w-full">
      <div class="text-center">
        <h1 class="text-4xl font-bold text-gray-800 mb-3">QR Code Generator</h1>
        <p class="text-gray-600 text-lg">
          Generate and manage your QR codes with ease
        </p>
      </div>

      <div
        class="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 transform transition-all duration-300 hover:shadow-2xl"
      >
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="space-y-3">
              <label class="block text-base font-semibold text-gray-700"
                >Campaign Name</label
              >
              <input
                type="text"
                v-model="formData.campaignName"
                class="mt-1 block w-full h-12 px-4 py-2 rounded-xl border-2 border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition duration-200 text-base placeholder-gray-400"
                required
                placeholder="Enter your campaign name"
              />
            </div>

            <div class="space-y-3">
              <label class="block text-base font-semibold text-gray-700"
                >API Key</label
              >
              <select
                v-model="formData.apiKey"
                class="mt-1 block w-full h-12 px-4 py-2 rounded-xl border-2 border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition duration-200 text-base bg-white"
                required
              >
                <option value="" disabled>Select an API Key</option>
                <option
                  v-for="apiKey in savedApiKeys"
                  :key="apiKey.badgeName"
                  :value="apiKey.apiKey"
                >
                  {{ apiKey.badgeName }}
                </option>
              </select>
            </div>

            <div class="space-y-3">
              <label class="block text-base font-semibold text-gray-700"
                >Number of QR Codes</label
              >
              <input
                type="number"
                v-model="formData.quantity"
                class="mt-1 block w-full h-12 px-4 py-2 rounded-xl border-2 border-gray-200 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition duration-200 text-base"
                min="1"
                required
              />
            </div>

            <div class="space-y-3">
              <label class="block text-base font-semibold text-gray-700"
                >Export Format</label
              >
              <div class="flex space-x-4">
                <button
                  type="button"
                  @click="generateZip"
                  class="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all duration-300 font-medium flex items-center justify-center space-x-2 text-base shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="loading"
                >
                  <i class="fas fa-file-archive"></i>
                  <span>Generate ZIP</span>
                </button>
                <button
                  type="button"
                  @click="generateCsv"
                  class="flex-1 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-300 font-medium flex items-center justify-center space-x-2 text-base shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="loading"
                >
                  <i class="fas fa-file-csv"></i>
                  <span>Generate CSV</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>

<script>
import { ref, inject, onMounted } from 'vue';
import Cookies from 'js-cookie';

export default {
  setup() {
    const api = inject('api');
    const utils = inject('utils');
    const savedApiKeys = ref([]);

    const formData = ref({
      campaignName: '',
      apiKey: '',
      quantity: 1,
    });

    const loading = ref(false);

    const loadApiKeys = () => {
      const savedKeys = Cookies.get('apiKeys');
      if (savedKeys) {
        savedApiKeys.value = JSON.parse(savedKeys);
      }
      console.log('savedApiKeys', savedApiKeys.value);
    };

    const generateLinks = async () => {
      loading.value = true;
      try {
        const badgeBot = new utils.BadgeBot(formData.value.apiKey);
        const response = await badgeBot.generateLinks(formData.value.quantity);
        const apiKey = savedApiKeys.value.find(
          (key) => key.apiKey === formData.value.apiKey
        );
        if (apiKey) {
          apiKey.generations = {
            links: response,
            campaignName: formData.value.campaignName,
            createdAt: new Date(),
          };
          Cookies.set('apiKeys', JSON.stringify(savedApiKeys.value), {
            expires: 365,
          });
        }
        return response;
      } catch (error) {
        console.error('Error generating links:', error);
        return null;
      } finally {
        loading.value = false;
      }
    };

    const generateZip = async () => {
      const links = await generateLinks();
      if (links) {
        const zipBlob = await utils.generateZip(links);
        utils.downloadFile(zipBlob, 'qr-codes.zip');
      }
      console.log('savedApiKeys', savedApiKeys.value);
    };

    const generateCsv = async () => {
      const links = await generateLinks();
      if (links) {
        const csvBlob = await utils.generateCsv(links);
        utils.downloadFile(csvBlob, 'liens.csv');
      }
      console.log('savedApiKeys', savedApiKeys.value);
    };

    onMounted(() => {
      loadApiKeys();
      // remove autoLink from Cookies
      //Cookies.remove('autoLink');
    });

    return {
      formData,
      loading,
      generateZip,
      generateCsv,
      savedApiKeys,
    };
  },
};
</script>

<style scoped>
html,
body {
  height: 100%;
  margin: 0;
}

.flex-grow {
  flex-grow: 1;
}

footer {
  @apply flex flex-col md:flex-row items-center space-y-2 md:space-y-0 space-x-0 md:space-x-2 py-8 justify-center border-t;
}

.footer a {
  @apply text-indigo-500 hover:underline;
}

.footer span {
  @apply text-gray-400;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

input:focus,
select:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style>
